import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useGoogleLogin } from '@react-oauth/google'
import logoIcon from '../../assets/logo-icon.png'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Card from '../../components/common/Card'
import { login, googleLogin } from '../../store/slices/authSlice'
import { authApi } from '../../services/api'
import { resolvePostAuthRoute } from '../../utils/accessFlow'

type View = 'email' | 'otp'

const LoginPage: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const hasGoogleClientId = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID)

  const redirectAfterLogin = (payload: any) => navigate(resolvePostAuthRoute(payload))

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')
    try {
      await authApi.sendOtp(email)
      setView('otp')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp || otp.length < 6) return
    setLoading(true)
    setError('')
    const result = await dispatch(login({ email, otp_code: otp }) as any)
    setLoading(false)
    if (login.fulfilled.match(result)) {
      redirectAfterLogin(result.payload)
    } else {
      const errorMsg = result.payload as string || 'Invalid OTP. Please try again.'
      setError(errorMsg)
      if (errorMsg.toLowerCase().includes('expired')) {
        setView('email')
        setOtp('')
      }
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    setResent(false)
    try {
      await authApi.sendOtp(email)
      setResent(true)
      setOtp('')
    } catch (err: any) {
      setError('Failed to resend OTP.')
    } finally {
      setResending(false)
    }
  }

  const handleGoogleSuccess = async (tokenResponse: any) => {
    console.log('[Auth] Google login response received, starting backend verification...')
    setLoading(true)
    setError('')
    const result = await dispatch(
      googleLogin({
        code: tokenResponse.code,
        redirect_uri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'postmessage',
      }) as any
    )
    setLoading(false)
    if (googleLogin.fulfilled.match(result)) {
      console.log('[Auth] Google login successful, redirecting user...')
      redirectAfterLogin(result.payload)
    } else {
      console.error('[Auth] Backend verification of Google token failed:', result.payload)
      setError(result.payload as string || 'Google login failed.')
    }
  }

  const signInWithGoogle = useGoogleLogin({
    onSuccess: r => {
      console.log('[Auth] Google OAuth popup success, obtained code.')
      handleGoogleSuccess(r)
    },
    onError: () => {
      console.warn('[Auth] Google sign-in was cancelled before completion.')
      setError('Google sign-in was cancelled before completion.')
    },
    onNonOAuthError: (err) => {
      console.error('[Auth] Google OAuth non-OAuth error:', err)
      if (err.type === 'popup_closed') {
        setError('Google sign-in was cancelled before completion.')
      } else if (err.type === 'popup_failed_to_open') {
        setError('Unable to open Google sign-in popup. Please allow popups and try again.')
      } else {
        setError('Google sign-in failed due to a network or browser error. Please try again.')
      }
    },
    flow: 'auth-code',
    scope: 'openid email profile',
  })

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-950 py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 group mb-4">
            <div className="relative">
              <img
                src={logoIcon}
                alt="AnalyzerView"
                className="w-14 h-14 object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="flex flex-col items-start">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                AnalyzerView
              </h1>
              <p className="text-xs text-gray-400 font-medium tracking-wide">RESEARCH PLATFORM</p>
            </div>
          </Link>
          <h2 className="text-2xl font-bold text-gray-100">Sign in to your account</h2>
          <p className="mt-2 text-sm text-gray-400">
            Or{' '}
            <Link to="/register" className="text-primary-400 hover:text-primary-300 font-medium">
              create a new account
            </Link>
          </p>
        </div>

        <Card>
          {view === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              {error && (
                <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}
              <Input
                label="Email address"
                type="email"
                name="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError('') }}
                placeholder="you@example.com"
                required
                autoFocus
              />
              <Button type="submit" loading={loading} className="w-full" size="lg">
                Send OTP
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-dark-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-dark-800 text-gray-500">or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!hasGoogleClientId) {
                    setError('Google login is not configured. Please contact support.')
                    return
                  }
                  signInWithGoogle()
                }}
                disabled={loading || !hasGoogleClientId}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-dark-600 rounded-md text-sm font-medium text-gray-200 bg-dark-700 hover:bg-dark-600 transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-400">
                  We sent a 6-digit code to <span className="text-gray-200 font-medium">{email}</span>
                </p>
                {resent && <p className="text-xs text-green-400 mt-1">A new OTP has been sent.</p>}
              </div>

              {error && (
                <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">One-Time Password</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '')); setError('') }}
                  placeholder="• • • • • •"
                  autoFocus
                  className="w-full bg-dark-800 border border-dark-600 text-gray-200 rounded-md px-4 py-3 text-2xl font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <Button type="submit" loading={loading} disabled={otp.length < 6} className="w-full" size="lg">
                Sign In
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setView('email'); setOtp(''); setError('') }}
                  className="text-gray-500 hover:text-gray-300"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-primary-400 hover:text-primary-300 disabled:opacity-50"
                >
                  {resending ? 'Sending…' : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}
        </Card>

        <p className="text-center text-xs text-gray-600">
          By signing in you agree to our{' '}
          <Link to="/terms" className="text-gray-500 hover:text-gray-400">Terms</Link> and{' '}
          <Link to="/privacy" className="text-gray-500 hover:text-gray-400">Privacy Policy</Link>
        </p>
      </div>
    </div>
  )
}

export default LoginPage
